import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react'
import CameraAltIcon from '@material-ui/icons/CameraAlt'
import DescriptionIcon from '@material-ui/icons/Description'
import {
  makeStyles,
  DialogActionArrows,
  Dialog,
  DialogActions,
  DialogContent,
  Button,
  DialogToggles,
  CalculatorIcon,
  BasketIcon,
  Checkbox,
  InputField,
  Typography,
  FormControlLabel,
  ConfirmationDialog,
  CompanyCamLogo
} from '@paintscout/ui'
import type { DialogToggleItem, StyleClasses, DialogProps, MuiTheme as Theme } from '@paintscout/ui'
import { Editor, useClientOptions, useDialogs, FormSection } from '@paintscout/ui-business'
import MediaIcon from '@material-ui/icons/PermMedia'
import type { QuoteItemConsumer, UpdateableLineItem, UpdateableProduct } from '@paintscout/util/builder'
import {
  getFilesForItem,
  updateFilesForItem,
  updateLineItem,
  hasIntegrationInterface,
  getFeature,
  getObjectLabels
} from '@paintscout/util/builder'
import type { QuoteDocument, QuoteFile, QuoteSource } from 'paintscout'
import { Detector } from 'react-detect-offline'
import { makeHtml, isLooselyEqual } from '@paintscout/util/util'
import { getLineItem } from '@paintscout/util/src/builder/quote/line-items/get-line-item/get-line-item'
import EditIntegrationItemFilesDialog from '../EditIntegrationItemFilesDialog'
import LineItemPricing from '../../forms/LineItemForm/LineItemPricing'
import MaterialsTable from '../EditAreaDialog/MaterialsTable'
import EditItemFilesDialog from '../EditItemFilesDialog'
import { calculateUpdateableLineItem } from '../../forms/LineItemForm/util'
import FilesGrid from '../../FilesGrid'
import { useQuote } from '../../context'
import EditLineItemDialogHeader from './EditLineItemDialogHeader'

export interface EditLineItemDialogProps extends DialogProps {
  classes?: DialogProps['classes'] & StyleClasses<typeof useStyles>
  quote: QuoteDocument
  item: UpdateableLineItem
  consumer: QuoteItemConsumer
  isNew?: boolean
  isInGroup?: boolean
  onConfirm: (quote: QuoteDocument, itemKey?: string) => void
  onCancel: (quote: QuoteDocument) => void
  cancelButtonLabel?: string
  nextItemKey?: string
  previousItemKey?: string
  isPainterView?: boolean
}

const useStyles = makeStyles<Theme, EditLineItemDialogProps>(
  (theme: Theme) => ({
    root: {},
    noteSection: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start'
    },
    nameSection: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      paddingTop: 0
    },
    nameInput: {
      '& input': {
        fontWeight: theme.typography.fontWeightMedium
      }
    },
    descriptionSection: {
      display: 'flex',
      flexDirection: 'column',
      paddingTop: 0
    },
    priceSection: {
      position: 'relative',
      marginBottom: theme.spacing(2)
    },
    contentInner: {},
    overflowVisible: {
      overflow: 'visible'
    }
  }),
  { name: 'EditLineItemDialog' }
)

function EditLineItemDialog(props: EditLineItemDialogProps) {
  const {
    quote,
    onCancel,
    onConfirm,
    nextItemKey,
    previousItemKey,
    item,
    consumer,
    isNew,
    isPainterView,
    cancelButtonLabel: _cancelButtonLabel,
    loading,
    ...baseDialogProps
  } = props
  const { openDialog, dismissDialog, dismissAllDialogs } = useDialogs()
  const { options } = useClientOptions()
  const { isNoteEditable, isEditable } = useQuote()
  const objectLabels = getObjectLabels({ options })
  const classes = useStyles(props)
  const features = getFeature({ options, path: 'quotes.areaSubstrates' })

  const itemKey = item.key
  const [updatedQuote, setUpdatedQuote] = useState<QuoteDocument>(quote)
  const [lineItem, setLineItem] = useState<UpdateableLineItem>({
    price: 0,
    hours: '',
    calculate: false,
    customHourlyRate: false,
    hourlyRate: 0,
    ...getLineItem({ quote, key: itemKey })
  })
  const descriptionRef = useRef(null)
  const crewNoteRef = useRef(null)
  const clientNoteRef = useRef(null)
  const nameRef = useRef(null)
  const [hideOnWorkOrder, setHideOnWorkOrder] = useState<boolean>(Boolean(item?.hideOnWorkOrder))

  const [files, setFiles] = useState(getFilesForItem({ quote, options, itemKey }))
  const showFiles = files.length > 0
  const isSmartTemplate = updatedQuote?.type === 'quoteTemplate' && updatedQuote?.smartTemplate
  const disabled = isSmartTemplate || !isEditable
  const isNoteDisabled = isSmartTemplate || !isNoteEditable

  const [showSections, setShowSections] = useState({
    showNotes:
      !isPainterView &&
      ((item && !!item.crewNote && consumer === 'crew') || (item && !!item.clientNote && consumer === 'customer')),
    showPricing: isPainterView || !!(item && (item.hours || item.price)),
    showMaterials: isPainterView || !!(item.materials && item.materials.length > 0)
  })

  useEffect(() => {
    setUpdatedQuote(quote)
    const updatedItem = getLineItem({ quote: updatedQuote, key: itemKey })
    descriptionRef?.current?.setHTML(updatedItem.description)
    crewNoteRef?.current?.setHTML(updatedItem.crewNote)
    clientNoteRef?.current?.setHTML(updatedItem.clientNote)
    if (nameRef.current) {
      nameRef.current.value = updatedItem.label
    }
    setLineItem(updatedItem)
    setHideOnWorkOrder(Boolean(updatedItem?.hideOnWorkOrder))
    setFiles(getFilesForItem({ quote: updatedQuote, options, itemKey }))
  }, [quote, item, itemKey])

  const handleFilesChange = (files: QuoteFile[], source?: QuoteSource, append?: boolean) => {
    // Update the files on that quote
    const newQuote = updateFilesForItem({
      quote: updatedQuote,
      options,
      itemKey,
      files,
      append: typeof append === 'undefined' ? true : append
    })

    if (source) {
      newQuote.sources = {
        ...newQuote.sources,
        [source.provider]: source
      }
    }

    setUpdatedQuote(newQuote)
    setFiles(getFilesForItem({ quote: newQuote, options, itemKey }))
  }

  const handleFileClick = useCallback(
    (files?: QuoteFile[], index?: number, uploadFiles?: File[], key?: string) => {
      if (key === 'showMedia' || key === 'device' || !key) {
        openDialog(EditItemFilesDialog, {
          files,
          index,
          onDelete: (_key, currentFiles) => {
            handleFilesChange(currentFiles, null, false)
          },
          onConfirm: (newFiles, _deletedFiles) => {
            handleFilesChange(newFiles, null, false)
            dismissDialog()
          },
          onCancel: dismissDialog
        })
      } else {
        openDialog(EditIntegrationItemFilesDialog, {
          provider: key,
          files,
          source: quote?.sources?.[key],
          onConfirm: (newFiles, deletedFiles, source) => {
            handleFilesChange(newFiles, source, true)
            dismissDialog()
          },
          onCancel: dismissDialog
        })
      }
    },
    [quote, handleFilesChange, props]
  )

  const handleToggleSection = useCallback(
    (key: string, _value: boolean) => {
      if (key === 'showMedia' || key === 'device' || key === 'companycam') {
        const files = getFilesForItem({ quote: updatedQuote, options, itemKey })
        handleFileClick(files, files.length, null, key)
      } else {
        setShowSections((prevShowSections) => ({
          ...prevShowSections,
          [key]: true // we're always setting this true as an experiment to have them appear, and then stay there as anchors for scrolling
        }))
      }
    },
    [quote, handleFileClick, getFilesForItem, setShowSections, props.item.key]
  )

  function syncedLineItem(): UpdateableLineItem {
    const name = nameRef?.current?.value ?? lineItem.name
    const description = descriptionRef.current?.getHTML() ?? lineItem.description
    const crewNote = crewNoteRef.current?.getHTML() ?? lineItem.crewNote
    const clientNote = clientNoteRef.current?.getHTML() ?? lineItem.clientNote

    let freshLineItem = lineItem
    const updatedLineItem = {
      label: name,
      description,
      crewNote,
      clientNote,
      hideOnWorkOrder,
      files: files.map((file) => file.key)
    }
    setLineItem((lineItem) => {
      freshLineItem = {
        ...lineItem,
        ...updatedLineItem
      }
      return lineItem
    })

    return freshLineItem
  }

  function getUpdatedQuote() {
    const updatedLineItem = syncedLineItem()
    return updateLineItem({
      quote: updatedQuote,
      item: updatedLineItem,
      options
    })
  }

  const handleDialogConfirm = () => {
    onConfirm(getUpdatedQuote())
  }

  const handleNavArrowClick = (navItemKey: string) => {
    onConfirm(getUpdatedQuote(), navItemKey)
  }

  const handleDialogCancel = () => {
    const hasChanges = !isLooselyEqual(quote, updatedQuote)

    if (hasChanges) {
      openDialog(ConfirmationDialog, {
        message: 'You have unsaved changes. Are you sure you want to close?',
        onConfirm: () => {
          dismissDialog() // Dismiss confirmation dialog
          dismissAllDialogs() // Close all dialogs
        },
        onCancel: () => {
          dismissDialog()
        }
      })
    } else {
      if (onCancel) {
        onCancel(getUpdatedQuote())
      } else {
        dismissAllDialogs()
      }
    }
  }

  const handleUpdateMaterials = (materials: UpdateableProduct[]) => {
    const updatedItem = {
      ...syncedLineItem(),
      materials
    }
    const calculatedItem = calculateUpdateableLineItem({ item: updatedItem, options, quote: updatedQuote })
    setLineItem(calculatedItem)
    setUpdatedQuote((updatedQuote) =>
      updateLineItem({
        quote: updatedQuote,
        item: calculatedItem,
        options
      })
    )
  }

  const handleCheckboxChange = (event: any, value: any) => {
    const { name } = event.target

    const updatedItem = {
      ...lineItem,
      [name]: value
    }

    if (name === 'customHourlyRate') {
      updatedItem.hourlyRate = null
    }

    const calculatedItem = calculateUpdateableLineItem({ item: updatedItem, options, quote })
    setLineItem(calculatedItem)
    setUpdatedQuote((updatedQuote) =>
      updateLineItem({
        quote: updatedQuote,
        item: calculatedItem,
        options
      })
    )
    setHideOnWorkOrder(value)
  }

  const handleReorder = useCallback(
    (files: QuoteFile[]) => {
      handleFilesChange(files)
    },
    [handleFilesChange]
  )

  const handleFieldChange = (field: keyof UpdateableLineItem, value: any) => {
    let updatedItem = lineItem
    setLineItem((lineItem) => {
      updatedItem = {
        ...lineItem,
        [field]: value
      }
      return updatedItem
    })
    const calculatedItem = calculateUpdateableLineItem({ item: updatedItem, options, quote })
    setUpdatedQuote((updatedQuote) =>
      updateLineItem({
        quote: updatedQuote,
        item: calculatedItem,
        options
      })
    )
  }

  function setItem(item: UpdateableLineItem) {
    setLineItem(item)
  }

  const MemoizedLineItemPricing = useMemo(
    () => LineItemPricing({ quote: updatedQuote, item: lineItem, initialItem: item, setItem, disabled }),
    [props, item, setItem]
  )

  return (
    <Dialog
      classes={{ paper: classes.overflowVisible }}
      open={true}
      maxWidth={'lg'}
      fullWidth={true}
      {...baseDialogProps}
    >
      <EditLineItemDialogHeader
        quote={updatedQuote}
        itemKey={itemKey}
        onCancel={handleDialogCancel}
        loading={loading}
      />
      <DialogContent>
        <form
          id="line-item-form"
          className={classes.contentInner}
          onSubmit={(e) => {
            e.preventDefault()
            handleDialogConfirm()
          }}
          onKeyDown={(keyEvent) => {
            if ((keyEvent.charCode || keyEvent.keyCode) === 13 && !(keyEvent.target as any).editor) {
              handleDialogConfirm()
            }
          }}
        >
          <div className={classes.root}>
            <FormSection classes={{ root: classes.nameSection }} hideDivider>
              <InputField
                fullWidth={true}
                label={'Name'}
                disabled={disabled}
                defaultValue={lineItem.label}
                name={'label'}
                autoFocus={true}
                classes={{ root: classes.nameInput }}
                inputRef={nameRef}
                onChange={(event) => handleFieldChange('label', event.target.value)}
              />
            </FormSection>
            <FormSection classes={{ root: classes.descriptionSection }} hideDivider>
              {features.areaViewDescription && (
                <Editor
                  label={'Description'}
                  disabled={disabled}
                  content={lineItem.description}
                  ref={descriptionRef}
                  fullWidth={true}
                  onChange={(value) => handleFieldChange('description', value)}
                />
              )}

              {!isPainterView && (
                <FormControlLabel
                  style={{ width: 'fit-content' }}
                  control={
                    <Checkbox
                      disabled={disabled}
                      checked={hideOnWorkOrder}
                      onChange={handleCheckboxChange}
                      name={'hideOnWorkOrder'}
                    />
                  }
                  label={`Hidden on ${objectLabels.workOrder.value}`}
                />
              )}
            </FormSection>

            <div className={'showPricing'}>
              {showSections.showPricing && (
                <FormSection
                  classes={{ root: classes.priceSection }}
                  style={{ paddingBottom: showSections.showMaterials ? 16 : 0 }}
                >
                  {MemoizedLineItemPricing}
                </FormSection>
              )}
            </div>

            {showSections.showNotes && (
              <div className={'showNotes'}>
                <FormSection classes={{ root: classes.noteSection }}>
                  <Editor
                    disabled={isNoteDisabled}
                    label={'Crew Note'}
                    fullWidth={true}
                    content={makeHtml(lineItem.crewNote, { replaceBreaks: true })}
                    ref={crewNoteRef}
                  />
                </FormSection>
                <FormSection classes={{ root: classes.noteSection }} hideDivider>
                  <Editor
                    disabled={disabled}
                    label={'Client Note'}
                    fullWidth={true}
                    content={makeHtml(lineItem.clientNote, { replaceBreaks: true })}
                    ref={clientNoteRef}
                  />
                </FormSection>
              </div>
            )}

            <div className={'showMaterials'}>
              {showSections.showMaterials && (
                <MaterialsTable
                  allowCustomProduct={true}
                  materials={item.materials}
                  onChange={handleUpdateMaterials}
                  options={options}
                  quoteTypes={quote.int_ext?.value ? [quote.int_ext.value] : []}
                  disabled={disabled}
                  checkParentHasChanges={() => !isLooselyEqual(quote, updatedQuote)}
                />
              )}
            </div>
          </div>
          <div className={'showMedia'}>
            {showFiles && (
              <FormSection>
                <Typography variant="h4">Media</Typography>
                <FilesGrid
                  files={files}
                  editable={true}
                  onReorder={handleReorder}
                  onFileClick={(index) => handleFileClick(files, index, null, 'device')}
                  onUpload={(newFiles) => {
                    if (newFiles && newFiles.length > 0) {
                      handleFileClick(files, files.length, newFiles)
                    }
                  }}
                />
              </FormSection>
            )}
          </div>
        </form>
      </DialogContent>
      <Detector>
        {(online) => {
          const toggles: DialogToggleItem[] = [
            { key: 'showPricing', label: 'Hours/Cost', value: showSections.showPricing, icon: CalculatorIcon },
            ...(!isPainterView
              ? [
                  {
                    key: 'showNotes',
                    label: 'Notes',
                    value: showSections.showNotes,
                    icon: DescriptionIcon
                  }
                ]
              : [])
          ]
          if (getFeature({ options, path: 'quotes.materialCost.enabled' })) {
            toggles.push({
              key: 'showMaterials',
              label: 'Materials',
              value: showSections.showMaterials,
              icon: BasketIcon
            })
          }
          if (getFeature({ options, path: 'media.enabled' })) {
            toggles.push({
              key: 'showMedia',
              label: 'Media',
              value: item.files.length > 0,
              icon: CameraAltIcon,
              disabled: !online || (disabled && !getFeature({ options, path: 'quotes.lineItemMediaOptions.enabled' })),
              subItems: hasIntegrationInterface({ options, integration: 'companycam' })
                ? [
                    {
                      key: 'companycam',
                      label: 'CompanyCam',
                      value: true,
                      icon: CompanyCamLogo
                    },
                    {
                      key: 'device',
                      label: 'My Device',
                      value: true,
                      icon: MediaIcon
                    }
                  ]
                : undefined
            })
          }

          return isPainterView ? null : <DialogToggles toggles={toggles} onToggle={handleToggleSection} scroll={true} />
        }}
      </Detector>
      <DialogActions
        arrows={
          <DialogActionArrows
            hidden={isNew || (!previousItemKey && !nextItemKey)}
            onPrevious={previousItemKey ? () => handleNavArrowClick(previousItemKey) : null}
            onNext={nextItemKey ? () => handleNavArrowClick(nextItemKey) : null}
          />
        }
      >
        <Button type="submit" form="line-item-form" variant={'contained'}>
          {isNew ? 'Confirm' : 'Done'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default EditLineItemDialog
